import type { Pagination } from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';
import { PaginationFooter as PaginationFooterComponent } from 'components/PaginationFooter';
import { useProfile } from 'init/use-enforce-profile';
import type { FC } from 'react';
import { type ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';
import { usePreviousPathname } from 'shared/atoms/previous-pathname';
import { getSessionStorageItem } from 'shared/utils/session-storage';

type UsePaginationOptions = {
  /**
   * A unique portion of the index page's URL path (e.g. 'products' for '/products').
   * Must also be present in the URLs of all child pages that flow from this index,
   * so that navigating between them preserves the current page.
   */
  indexPath: string;
  overridePageLimit?: number;
};

type UsePaginationReturn = {
  currentPage: number;
  pageLimit: number;
  resetPagination: VoidFunction;
  PaginationFooter: FC<ComponentProps<typeof Pagination>>;
};

/**
 * Custom hook for managing pagination state. Use in conjunction with PaginationFooter.
 * Current page is persisted in sessionStorage and restored only when navigating
 * within the same module (i.e. the previous pathname includes indexPath).
 * @returns An object containing pagination state and components.
 */
export function usePagination({
  indexPath,
  overridePageLimit,
}: UsePaginationOptions): UsePaginationReturn {
  const profile = useProfile();
  const previousPathname = usePreviousPathname();
  const pageStorageKey = `${indexPath}CurrentPage`;

  const isNavigatingWithinModule = previousPathname.includes(indexPath);

  const [currentPage, setCurrentPage] = useState(() => {
    if (isNavigatingWithinModule) return getSessionStorageItem(pageStorageKey, 1);
    window.sessionStorage.removeItem(pageStorageKey);
    return 1;
  });
  const [pageLimit, setPageLimit] = useLocalStorage({
    key: `${indexPath}PageLimit`,
    defaultValue: overridePageLimit ?? profile.preferences.itemsPerPage,
  });

  const setCurrentPageWithPersist = useCallback(
    (page: number) => {
      setCurrentPage(page);
      window.sessionStorage.setItem(pageStorageKey, String(page));
    },
    [pageStorageKey],
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const page = searchParams.get('page');
    const pageLimitParam = searchParams.get('page_limit');

    if (page) setCurrentPageWithPersist(Number(page));
    if (pageLimitParam) setPageLimit(Number(pageLimitParam));
  }, [setPageLimit, setCurrentPageWithPersist]);

  const PaginationFooter = useMemo(
    () =>
      function PaginationFooterWrapper(props: ComponentProps<typeof Pagination>) {
        return (
          <PaginationFooterComponent
            currentPage={currentPage}
            pageLimit={pageLimit}
            setCurrentPage={setCurrentPageWithPersist}
            setPageLimit={setPageLimit}
            {...props}
          />
        );
      },
    [currentPage, pageLimit, setCurrentPageWithPersist, setPageLimit],
  );

  const resetPagination = useCallback(
    () => setCurrentPageWithPersist(1),
    [setCurrentPageWithPersist],
  );

  return { currentPage, pageLimit, resetPagination, PaginationFooter };
}
